import { supabase } from '../supabaseClient';

/**
 * Billing is metered per active user with a per-plan floor. Nothing in this
 * service computes a price: every figure comes from the database, which is
 * also what the Stripe meter is built from, so the number an admin sees here
 * and the number on the invoice cannot drift apart.
 */

export type PlanCode = 'legacy' | 'core' | 'advanced' | 'enterprise';

export type EntitlementKey =
  | 'rostering'
  | 'patrols'
  | 'geofencing'
  | 'licence_verification'
  | 'incident_reports'
  | 'welfare_checks'
  | 'emergency_alarm'
  | 'live_tracking'
  | 'admin_dashboard'
  | 'guard_app'
  | 'operational_analytics'
  | 'location_history'
  | 'payroll_export'
  | 'audit_reporting'
  | 'priority_support'
  | 'client_portals';

export type TaxTreatment = 'domestic_vat' | 'uk_reverse_charge' | 'eu_reverse_charge';

export interface BillingSummary {
  planCode: PlanCode;
  planName: string;
  planIsMetered: boolean;
  billingInterval: 'month' | 'year';
  billingMode: string;
  billingStatus: string;
  currency: string;
  unitAmountCents: number | null;
  taxTreatment: TaxTreatment;
  vatNumber: string | null;
  trialEndsAt: string | null;
  periodStart: string;
  periodEnd: string;
  daysRemaining: number;
  activeGuards: number;
  activeStaff: number;
  activeUsers: number;
  floorUsers: number;
  billableUsers: number;
  committedUserCount: number | null;
  /** Ex-VAT, like every figure this system quotes. */
  projectedAmountCents: number | null;
}

export interface BillingUsagePeriod {
  id: string;
  periodStart: string;
  periodEnd: string;
  planCode: PlanCode;
  activeGuards: number;
  activeStaff: number;
  activeUsers: number;
  floorUsers: number;
  billableUsers: number;
  unitAmountCents: number;
  amountCents: number;
  currency: string;
  status: 'closed' | 'reported' | 'invoiced' | 'void';
  reportedAt: string | null;
}

export interface BillingTrueUp {
  id: string;
  quarterStart: string;
  quarterEnd: string;
  committedUsers: number;
  measuredMonths: number;
  /** Recorded for the conversation, never charged. */
  peakUsers: number;
  averageUsers: number;
  chargeableUsers: number;
  unitAmountCents: number;
  amountCents: number;
  currency: string;
  status: 'pending' | 'invoiced' | 'waived' | 'void';
}

interface SummaryRow {
  plan_code: PlanCode;
  plan_name: string;
  plan_is_metered: boolean;
  billing_interval: 'month' | 'year';
  billing_mode: string;
  billing_status: string;
  currency: string;
  unit_amount_cents: number | null;
  tax_treatment: TaxTreatment;
  vat_number: string | null;
  trial_ends_at: string | null;
  period_start: string;
  period_end: string;
  days_remaining: number;
  active_guards: number;
  active_staff: number;
  active_users: number;
  floor_users: number;
  billable_users: number;
  committed_user_count: number | null;
  projected_amount_cents: number | null;
}

interface UsagePeriodRow {
  id: string;
  period_start: string;
  period_end: string;
  plan_code: PlanCode;
  active_guards: number;
  active_staff: number;
  active_users: number;
  floor_users: number;
  billable_users: number;
  unit_amount_cents: number;
  amount_cents: number;
  currency: string;
  status: BillingUsagePeriod['status'];
  reported_at: string | null;
}

interface TrueUpRow {
  id: string;
  quarter_start: string;
  quarter_end: string;
  committed_users: number;
  measured_months: number;
  peak_users: number;
  average_users: string | number;
  chargeable_users: number;
  unit_amount_cents: number;
  amount_cents: number;
  currency: string;
  status: BillingTrueUp['status'];
}

function toSummary(row: SummaryRow): BillingSummary {
  return {
    planCode: row.plan_code,
    planName: row.plan_name,
    planIsMetered: row.plan_is_metered,
    billingInterval: row.billing_interval,
    billingMode: row.billing_mode,
    billingStatus: row.billing_status,
    currency: row.currency,
    unitAmountCents: row.unit_amount_cents,
    taxTreatment: row.tax_treatment,
    vatNumber: row.vat_number,
    trialEndsAt: row.trial_ends_at,
    periodStart: row.period_start,
    periodEnd: row.period_end,
    daysRemaining: row.days_remaining,
    activeGuards: row.active_guards,
    activeStaff: row.active_staff,
    activeUsers: row.active_users,
    floorUsers: row.floor_users,
    billableUsers: row.billable_users,
    committedUserCount: row.committed_user_count,
    projectedAmountCents: row.projected_amount_cents,
  };
}

function toUsagePeriod(row: UsagePeriodRow): BillingUsagePeriod {
  return {
    id: row.id,
    periodStart: row.period_start,
    periodEnd: row.period_end,
    planCode: row.plan_code,
    activeGuards: row.active_guards,
    activeStaff: row.active_staff,
    activeUsers: row.active_users,
    floorUsers: row.floor_users,
    billableUsers: row.billable_users,
    unitAmountCents: row.unit_amount_cents,
    amountCents: row.amount_cents,
    currency: row.currency,
    status: row.status,
    reportedAt: row.reported_at,
  };
}

function toTrueUp(row: TrueUpRow): BillingTrueUp {
  return {
    id: row.id,
    quarterStart: row.quarter_start,
    quarterEnd: row.quarter_end,
    committedUsers: row.committed_users,
    measuredMonths: row.measured_months,
    peakUsers: row.peak_users,
    averageUsers: Number(row.average_users),
    chargeableUsers: row.chargeable_users,
    unitAmountCents: row.unit_amount_cents,
    amountCents: row.amount_cents,
    currency: row.currency,
    status: row.status,
  };
}

export const billingService = {
  /**
   * The live counter. Returns null for a user whose profile has no company or
   * who is not staff, which the page renders as "unavailable" rather than as
   * a zero that would read like a free month.
   */
  async fetchSummary(): Promise<BillingSummary | null> {
    const { data, error } = await supabase.rpc('current_billing_summary');
    if (error) throw error;
    return data ? toSummary(data as SummaryRow) : null;
  },

  async fetchUsageHistory(limit = 12): Promise<BillingUsagePeriod[]> {
    const { data, error } = await supabase
      .from('billing_usage_periods')
      .select(
        'id,period_start,period_end,plan_code,active_guards,active_staff,active_users,'
          + 'floor_users,billable_users,unit_amount_cents,amount_cents,currency,status,reported_at',
      )
      .neq('status', 'void')
      .order('period_start', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return ((data ?? []) as unknown as UsagePeriodRow[]).map(toUsagePeriod);
  },

  async fetchTrueUps(limit = 8): Promise<BillingTrueUp[]> {
    const { data, error } = await supabase
      .from('billing_true_ups')
      .select(
        'id,quarter_start,quarter_end,committed_users,measured_months,peak_users,'
          + 'average_users,chargeable_users,unit_amount_cents,amount_cents,currency,status',
      )
      .neq('status', 'void')
      .order('quarter_start', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return ((data ?? []) as unknown as TrueUpRow[]).map(toTrueUp);
  },

  async fetchEntitlements(): Promise<EntitlementKey[]> {
    const { data, error } = await supabase.rpc('current_company_entitlements');
    if (error) throw error;
    return ((data ?? []) as { entitlement_key: EntitlementKey }[]).map((row) => row.entitlement_key);
  },

  /**
   * Records that this admin or manager used the portal today. Idempotent per
   * user per day, so calling it on every session resolve costs one no-op
   * upsert. A failure is swallowed: a metering write must never be the reason
   * someone cannot reach their workspace, and a nightly server-side sweep of
   * auth.users catches anything this misses.
   */
  async recordSignIn(): Promise<void> {
    const { error } = await supabase.rpc('record_portal_sign_in');
    if (error) console.warn('Unable to record portal sign-in for billing', error);
  },
};
